<template>
    <Slide :title="title" :subtitle="subtitle">
        <div class="cr:grid cr:grid-cols-1 cr:md:grid-cols-2 cr:gap-5">
          <div>
                <p>First Name*</p>
                <CustomInput
                    :placeholder="'Enter first name'"
                    v-model="registrationStore.registeringCompany.firstName"
                    :name="'first-name'"
                    :autocomplete="'given-name'"
                />
            </div>
            <div>
                <p>Last Name*</p>
                <CustomInput
                    :placeholder="'Enter last name'"
                    v-model="registrationStore.registeringCompany.lastName"
                    :name="'last-name'"
                    :autocomplete="'family-name'"
                />
            </div>
            <div>
                <p>Phone Number*</p>
                <CustomInput
                    :placeholder="'212 555 0188'"
                    v-model="registrationStore.registeringCompany.phone"
                    :input-type="'tel'"
                    :autocomplete="'tel'"
                    :name="'phone'"
                />
            </div>
            <div>
                <p>Email*</p>
                <CustomInput
                    :placeholder="'name@example.com'"
                    v-model="registrationStore.registeringCompany.email"
                    :name="'email'"
                    :autocomplete="'email'"
                    :input-type="'email'"
                />
            </div>
            <div>
                <p>Trading Name*</p>
                <CustomInput
                    :placeholder="'ACME'"
                    v-model="registrationStore.registeringCompany.companyName"
                />
            </div>
            <div>
                <p>Website*</p>
                <CustomInput
                    :placeholder="'example.com'"
                    v-model="registrationStore.registeringCompany.website"
                    :input-type="'url'"
                    :autocomplete="'url'"
                    :name="'website'"
                />
            </div>
            <div class="cr:md:col-span-2 cr:hidden">
                <p>Primary Industries*:</p>
                <div class="cr:grid cr:grid-cols-3 cr:md:grid-cols-4 cr:text-sm">
                    <div v-for="industry in registrationStore.industries" class="cr:flex cr:items-center cr:gap-2 cr:p-1">
                        <CustomCheckbox v-model="industry.selected"/>
                        <p>{{ industry.name }}</p>
                    </div>
                </div>
            </div>
        </div>
    </Slide>
</template>

<script setup lang="ts">

import CustomInput from "@/components/elements/CustomInput.vue";
import {useRegistrationStore} from "@/stores/registration.ts";
import CustomCheckbox from "@/components/elements/CustomCheckbox.vue";
import {computed, onMounted, onBeforeMount, type Ref, ref} from "vue";
import Validation from "@/services/Validation.ts";
import Dropdown from "@/components/elements/Dropdown.vue";
import type MultiSelectOption from "@/interfaces/MultiSelectOption.ts";
import MultiSelect from "@/components/elements/MultiSelect.vue";
import Slide from "@/components/slides/Slide.vue";
import {useErrorStore} from "@/stores/error.ts";

const props = defineProps({
    title: {
        type: String,
        default: 'Register To Purchase Leads'
    },
    subtitle: {
        type: String,
        default: "Add some basic information about your company"
    }
});

const registrationStore = useRegistrationStore();
const errorStore = useErrorStore();
const count = ref(0);
const autocomplete: Ref<google.maps.places.Autocomplete | null> = ref(null);
const stateOptions: Ref<MultiSelectOption[]> = ref([]);

onMounted(() => initialize())

const canContinue = computed(() => true);

const slidePayload = computed(() => {
    return {
        first_name: registrationStore.registeringCompany.firstName,
        last_name: registrationStore.registeringCompany.lastName,
        phone: registrationStore.registeringCompany.phone,
        email: registrationStore.registeringCompany.email,
        company_name: registrationStore.registeringCompany.companyName,
        website: registrationStore.registeringCompany.website,
        industries: registrationStore.industries.filter(industry => industry.selected).map(industry => industry.slug),
    }
});

const validate = () => {
    const company = registrationStore.registeringCompany;

    if (!company.firstName || company.firstName.length < 2) {
        errorStore.report('Please enter a valid first name.')
        return false;
    }

    if (!company.lastName || company.lastName.length < 2) {
        errorStore.report('Please enter a valid last name.')
        return false;
    }

    if (!company.phone || !Validation.validPhone(company.phone)) {
        errorStore.report('Please enter a valid US phone.')
        return false;
    }

    if (!company.email || !Validation.validEmail(company.email)) {
        errorStore.report('Please enter a valid email.')
        return false;
    }

    if (!company.companyName || company.companyName.length < 2) {
        errorStore.report('Please enter a valid trading name.')
        return false;
    }

    if (!company.website || !Validation.validUrl(company.website)) {
        errorStore.report('Please enter a valid website.')
        return false;
    }

    return true;
}

defineExpose({
    canContinue,
    slidePayload,
    validate
});

const initialize = () => {
    // initializeAutoComplete();
    stateOptions.value = registrationStore.states.map(state => {
        return {
            id: state.abbr,
            name: state.name,
        }
    });
  loadCalendly()
}

const loadCalendly = () => {
  if (Array.from(document.getElementsByTagName('script')).some(sc => sc.src=='https://assets.calendly.com/assets/external/widget.js') === false) {
    let tag = document.createElement("script");
    let style = document.createElement("link")
    style.setAttribute("href", "https://assets.calendly.com/assets/external/widget.css")
    style.setAttribute("rel", "stylesheet")
    tag.setAttribute("src", "https://assets.calendly.com/assets/external/widget.js");
    document.head.appendChild(tag);
    document.head.appendChild(style);
  }
}

const initializeAutoComplete = () => {
    if (!registrationStore.googleService.initialized) {
        if (count.value >= 100) {
            return;
        }

        setTimeout(() => initializeAutoComplete(), 200)

        count.value++;

        return;
    }

    const container = document.querySelector('#address-input');
    const map = registrationStore.googleService.getMapService();

    autocomplete.value = new map.places.Autocomplete(container as HTMLInputElement, {
        componentRestrictions: {
            country: 'us',
        },
        types: ['address']
    });

    autocomplete.value.addListener('place_changed', () => {
        handlePlaceChange();
    })
}

const handlePlaceChange = () => {
    const place = autocomplete.value?.getPlace();

    if (!place) {
        console.error('Autocomplete does not have place object')
        return;
    }

    let streetAddress = '';
    let city = '';

    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
        const componentType = component.types[0];

        switch (componentType) {
            case 'street_number':
                streetAddress = `${component.long_name} ${streetAddress}`;
                break;
            case 'route':
                streetAddress += component.short_name;
                break;
            case 'postal_code':
                registrationStore.registeringCompany.address.zipCode = component.long_name;
                break;
            case 'locality':
                city = component.long_name;
                break;
            case 'sublocality_level_1':
                if (!city) {
                    city = component.long_name
                }
                break;
            case "administrative_area_level_1": {
                registrationStore.registeringCompany.address.state = component.short_name;
                break;
            }
        }
    }

    registrationStore.registeringCompany.address.streetAddress = streetAddress;
    registrationStore.registeringCompany.address.city = city;
}

</script>